/**
 * The low / current / high row that sits ABOVE a `CresultcardTrack`.
 *
 * A track shows where a value sits between two bounds. On its own it cannot
 * say what those bounds ARE, and a reader who cannot name the endpoints cannot
 * act on the position — "somewhere in the upper third" is not a fact. This is
 * the row that names them, and it is a separate component rather than three
 * more props on the track because only some tracks have a named scale: a
 * daylight bar has ends, not endpoints with figures.
 *
 * The current mark is centred on its own fraction using the standard
 * offset-then-counter-translate pair (`insetInlineStart: f`, then a translate
 * of `−f` of the mark's OWN width). That is the one placement that is exactly
 * centred at every fraction and still cannot overflow either edge, so the
 * mark never has to be clamped away from the position it is reporting.
 *
 * The current reading gets its own strip above the two bounds rather than
 * sharing their row. A reading near the 52-week high genuinely sits at the far
 * end of the scale, so a shared row collides exactly when the position is most
 * interesting; the caret under the bounds is what ties the floating mark back
 * down to the track.
 */
"use client";
import type { CSSProperties, ReactElement, ReactNode } from "react";
import {
  CRC_NUMERIC_STYLE,
  CRC_STACK_GAP,
  CRC_TEXT_MUTED,
  CRC_TONE_COLOR,
  CRC_TYPE_STYLE,
} from "./cresultcard.tokens";
import {
  CresultcardStackDensity,
  CresultcardTextStep,
  CresultcardTone,
} from "./cresultcard.types";

const CARET_WIDTH = 8;
const CARET_HEIGHT = 5;
const CARET_PATH = "M 0 0 L 8 0 L 4 5 Z";
const CARET_VIEWBOX = "0 0 8 5";
const MARK_GAP = 2;
const ROW_GAP = 10;
const PERCENT = 100;
const RATIO_PRECISION = 3;

/** Which of the three the mark is. It decides alignment, never size. */
enum ScaleSlot {
  Low = "low",
  Current = "current",
  High = "high",
}

/**
 * CSS values live in a keyed map rather than on the enum, because csstype's
 * `textAlign` is a closed literal union and a string-enum member is not
 * assignable to it.
 */
const SLOT_TEXT_ALIGN: Record<ScaleSlot, CSSProperties["textAlign"]> = {
  [ScaleSlot.Low]: "start",
  [ScaleSlot.Current]: "center",
  [ScaleSlot.High]: "end",
};

const SLOT_ALIGN_ITEMS: Record<ScaleSlot, CSSProperties["alignItems"]> = {
  [ScaleSlot.Low]: "flex-start",
  [ScaleSlot.Current]: "center",
  [ScaleSlot.High]: "flex-end",
};

/** One named end of the scale, or the reading between them. */
export interface CresultcardScaleMark {
  readonly label: string;
  readonly value: ReactNode;
}

interface ScaleMarkProps {
  readonly mark: CresultcardScaleMark;
  readonly slot: ScaleSlot;
  readonly color: string;
}

function CresultcardScaleMarkBlock({
  mark,
  slot,
  color,
}: ScaleMarkProps): ReactElement {
  return (
    <span
      data-el="Cresultcard/ScaleMarkBlock/Root"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: SLOT_ALIGN_ITEMS[slot],
        gap: MARK_GAP,
        minWidth: 0,
      }}
    >
      <span
        style={{
          ...CRC_TYPE_STYLE[CresultcardTextStep.Eyebrow],
          color: CRC_TEXT_MUTED,
          whiteSpace: "nowrap",
          textAlign: SLOT_TEXT_ALIGN[slot],
        }}
      >
        {mark.label}
      </span>
      <span
        style={{
          ...CRC_TYPE_STYLE[CresultcardTextStep.Body],
          ...CRC_NUMERIC_STYLE,
          color,
          whiteSpace: "nowrap",
          textAlign: SLOT_TEXT_ALIGN[slot],
        }}
      >
        {mark.value}
      </span>
    </span>
  );
}

interface CresultcardScaleProps {
  readonly low: CresultcardScaleMark;
  readonly current: CresultcardScaleMark;
  readonly high: CresultcardScaleMark;
  /** Where `current` sits between `low` and `high`, as a 0–1 share. */
  readonly fraction: number;
  readonly tone?: CresultcardTone;
}

export function CresultcardScale({
  low,
  current,
  high,
  fraction,
  tone = CresultcardTone.Positive,
}: CresultcardScaleProps): ReactElement {
  const clamped = Math.min(Math.max(fraction, 0), 1);
  const offset = `${(clamped * PERCENT).toFixed(RATIO_PRECISION)}%`;
  const counter = `${(-clamped * PERCENT).toFixed(RATIO_PRECISION)}%`;
  const color = CRC_TONE_COLOR[tone];

  return (
    <div
      data-el="Cresultcard/Scale/Root"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: CRC_STACK_GAP[CresultcardStackDensity.Tight],
        width: "100%",
        minWidth: 0,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "flex-start",
          width: "100%",
          minWidth: 0,
        }}
      >
        <span
          style={{
            position: "relative",
            insetInlineStart: offset,
            display: "block",
            transform: `translate3d(${counter}, 0, 0)`,
          }}
        >
          <CresultcardScaleMarkBlock
            color={color}
            mark={current}
            slot={ScaleSlot.Current}
          />
        </span>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: ROW_GAP,
          minWidth: 0,
        }}
      >
        <CresultcardScaleMarkBlock
          color={CRC_TEXT_MUTED}
          mark={low}
          slot={ScaleSlot.Low}
        />
        <CresultcardScaleMarkBlock
          color={CRC_TEXT_MUTED}
          mark={high}
          slot={ScaleSlot.High}
        />
      </div>

      <span
        aria-hidden
        style={{
          position: "relative",
          display: "block",
          width: "100%",
          height: CARET_HEIGHT,
        }}
      >
        <svg
          style={{
            position: "absolute",
            insetBlockStart: 0,
            insetInlineStart: offset,
            display: "block",
            width: CARET_WIDTH,
            height: CARET_HEIGHT,
            transform: "translate3d(-50%, 0, 0)",
          }}
          viewBox={CARET_VIEWBOX}
        >
          <path
            d={CARET_PATH}
            fill={color}
          />
        </svg>
      </span>
    </div>
  );
}
